// dependencies
import { useAutoAnimate } from "@formkit/auto-animate/react";
import * as Sentry from "@sentry/react";
import React, { ReactNode, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

// components
import { Avatar } from "@performyard/py-components";

import Button from "../../../components/Button";
import Card from "../../../components/Card";
import Container from "../../../components/Container";
import Spacer from "../../../components/Spacer";
import ComponentLoader from "../../../components/Spinner/ComponentLoader";
import Text from "../../../components/Text";
import Typography from "../../../components/Text";
import EditFieldModal from "./components/EditFieldModal";
//types
import { EditFieldForm } from "./components/EditFieldModal";
import FieldHistoryItem from "./components/FieldHistoryItem";
import FieldHistoryItemErrorFallback from "./components/FieldHistoryItem/FieldHistoryItemErrorFallback";
import LineChart from "./components/LineChart";

// hooks
import {
	useDeleteEmployeeCustomFieldHistory,
	useEmployeeCustomField,
	useEmployeeCustomFieldHistory,
	useMutateEmployeeCustomFields,
} from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useEmployeeDashboardOrigin } from "../../../hooks/useEmployeeDashboardOrigin";

// utils
import { buildFieldHistoryBreadcrumbs } from "../../../utils/employeeDetailsBreadcrumbs";
import getPossessiveNameCase from "../../../utils/getPossessiveNameCase";
import { getIntegrationImage, getValueForUpdate } from "../utils/index";

// template
import FormPageTemplate from "../../../pageTemplates/FormPageTemplate";
// pages
import ErrorPage from "../../Error/ErrorPage";
// styles
import css from "./ViewFieldHistory.module.scss";

const fieldsNotClearable = ["Email"];

const ViewFieldHistory = () => {
	const [isEditFieldModalOpen, setIsEditFieldModalOpen] = useState(false);
	const { userID = "", fieldID = "" } = useParams<{ userID: string; fieldID: string }>() || "";
	const location = useLocation();
	const isNewNav = useIsNewNavigation();
	const dashboardOrigin = useEmployeeDashboardOrigin({ deep: true });
	const [animationParent] = useAutoAnimate();
	const [animationParentGraph] = useAutoAnimate();

	// hooks
	const {
		isLoading: loadingFieldHistoryData,
		data: fieldHistoryData,
		isError: fieldHistoryError,
		error: fieldHistoryErrorData,
	} = useEmployeeCustomFieldHistory(userID, fieldID);
	const {
		data: fieldData,
		isLoading: loadingFieldData,
		isError: fieldError,
		error: fieldDataHistoryErrorData,
	} = useEmployeeCustomField(userID, fieldID);
	const { mutateAsync: deleteEmployeeCustomFieldHistory, isPending: pendingDeleteCustomFieldHistory } =
		useDeleteEmployeeCustomFieldHistory(userID);
	const { mutateAsync: updateEmployeeCustomFields, isPending: updatingField } = useMutateEmployeeCustomFields();
	if (fieldHistoryError || fieldError) {
		let errorCode = null;

		if (fieldHistoryError) {
			errorCode = fieldHistoryErrorData["response"]["status"];
		}
		if (fieldError) {
			errorCode = fieldDataHistoryErrorData["response"]["status"];
		}
		return <ErrorPage code={errorCode || 500} />;
	}

	const handleSubmitEditFieldForm = (formValues: EditFieldForm) => {
		const formattedFieldValue = getValueForUpdate(fieldHistoryData?.type || "", formValues.fieldValue);
		updateEmployeeCustomFields(
			{
				updates: [
					{
						_id: fieldID,
						value: formattedFieldValue,
						comment: formValues.comment,
					},
				],
				asOf: formValues.effectiveDate || new Date(),
				employeeId: userID,
			},
			{
				onSuccess: () => {
					setIsEditFieldModalOpen(false);
				},
			}
		);
	};

	const onRemoveHistoryItem = (historyItemID: string) => {
		if (!pendingDeleteCustomFieldHistory) {
			deleteEmployeeCustomFieldHistory(historyItemID);
		}
	};

	if (loadingFieldHistoryData || loadingFieldData) {
		return <ComponentLoader />;
	}

	const userExternalIdKey = fieldData?.user?.externalIds && Object.keys(fieldData?.user?.externalIds)?.[0];
	const userIsActivelyConnected = userExternalIdKey ? fieldData?.integrations?.[userExternalIdKey] : false;

	let isFieldEditable = fieldData?.editable && !fieldData?.isScimManaged && !userIsActivelyConnected;

	// check if field is clearable
	if (fieldHistoryData?.history.length === 1 && fieldsNotClearable.includes(fieldData?.name || "")) {
		isFieldEditable = false;
	}

	let heading: ReactNode | null = <Button onClick={() => setIsEditFieldModalOpen(true)}>Edit Field</Button>;

	const isIntegrationManaged =
		userIsActivelyConnected &&
		fieldData?.integrations &&
		Object.values(fieldData?.integrations)?.some((hasIntegration) => hasIntegration);

	if (fieldData?.isScimManaged) {
		heading = (
			<Container gap="4px">
				<Typography size="paragraph-3" weight="regular" color="neutral-7">
					This field is managed by{" "}
				</Typography>
				<img src={getIntegrationImage({ scim: true }) || ""} alt="SCIM" className={css.titleImage} />
			</Container>
		);
	} else if (isIntegrationManaged) {
		heading = (
			<Container gap="4px">
				<Typography size="paragraph-3" weight="regular">
					This field is managed by{" "}
				</Typography>
				<img
					src={getIntegrationImage(fieldData.integrations || {}) || ""}
					alt="Integration"
					className={css.titleImage}
				/>
			</Container>
		);
	} else if (!fieldData?.editable) {
		heading = null;
	}

	const main = (
		<Card>
			{/* buttons container */}
			<Container gap="8px" verticalAlignment="center">
				{heading}
			</Container>

			<Spacer height={4} />

			{/* field history title */}
			<Container gap="4px">
				<Text size="sub-heading" weight="regular">
					{"History of"}
				</Text>
				<Avatar
					dangerouslySetClassName={css.titleAvatar}
					size={32}
					id={userID}
					name={fieldData?.user?.name || "User"}
				/>
				<Text size="sub-heading" weight="regular">
					{getPossessiveNameCase(fieldData?.user?.name || "")}
				</Text>
				<Text size="sub-heading" weight="semi-bold">
					{fieldHistoryData?.name || ""}
				</Text>
			</Container>

			<Spacer height={4} />

			{/* trendline chart */}
			{(fieldData?.type === "monetary" || fieldData?.type === "numeric" || fieldData?.type === "salary") &&
			fieldHistoryData?.history.length &&
			fieldHistoryData.history.length > 1 ? (
				<div ref={animationParentGraph}>
					<LineChart fieldHistory={fieldHistoryData?.history} fieldTitle={fieldHistoryData?.name || ""} />
					<Spacer height={4} />
				</div>
			) : null}

			{/* field history items */}
			<Container column gap="25px" ref={animationParent} testId="field-history-container">
				{fieldHistoryData?.history.map((historyItem) => (
					<Sentry.ErrorBoundary
						key={historyItem._id.toString()}
						fallback={<FieldHistoryItemErrorFallback fieldName={fieldHistoryData?.name} />}
						onError={() => {
							Sentry.setContext("historyItem", {
								historyItemId: historyItem._id,
								fieldId: fieldID,
								fieldName: fieldHistoryData?.name,
								fieldType: fieldHistoryData?.type,
								employeeId: userID,
								asOf: historyItem.asOf,
							});
						}}
					>
						<FieldHistoryItem
							historyItem={historyItem}
							fieldType={fieldHistoryData?.type || "unknown"}
							onRemoveHistoryItem={onRemoveHistoryItem}
							isEditable={(isFieldEditable && !isIntegrationManaged) || false}
						/>
					</Sentry.ErrorBoundary>
				))}
			</Container>
			<EditFieldModal
				fieldData={fieldData}
				isEditFieldModalOpen={isEditFieldModalOpen}
				setIsEditFieldModalOpen={setIsEditFieldModalOpen}
				submitEditFieldForm={handleSubmitEditFieldForm}
				updatingField={updatingField}
			/>
		</Card>
	);
	const fieldName = fieldData?.name || "Field";
	const breadcrumbs = isNewNav
		? buildFieldHistoryBreadcrumbs({
				search: location.search,
				dashboardOrigin,
				userId: userID,
				fieldId: fieldID,
				fieldName,
			})
		: [
				{ pageID: "dashboard", label: "Dashboard", url: "/" },
				{ pageID: "employee-details", label: "Employee Details", url: `/details/${userID}` },
				{
					pageID: "view-field-history",
					label: `History of ${fieldName}`,
					url: `/view-field-history/${fieldID}`,
				},
			];
	return <FormPageTemplate title="Field History" mainColumn={[main]} breadcrumbs={breadcrumbs} />;
};

export default ViewFieldHistory;
